import React, { useMemo, useState } from 'react';
import Icon from '../ui/Icon';
import { KpiCard, PageSkeleton } from '../ui/primitives';
import { initialsOf, fmtDuration } from '../ui/couriers';
import DeliveryMap from './DeliveryMap';
import {
  useDashboardStats, useActiveStations, useActiveSessions, usePackingHistory,
  useAdminOrders, useDeliveries, useTrackedParcels, useLiveTracking, useLiveFeed, useSenderHub, useDestinationRankings,
  ActiveStation, ActiveSession, HistoryRow, RankRow, useShipmentsInTransit, useDispatchedParcels,} from './hooks';
import { useOrg } from '../contexts/OrgContext';
import { useAdminToast } from './AdminShell';
import axios from 'axios';
import { API_URL } from '../config/api';

export const FEED_ICON: Record<string, [string, string, string]> = {
  packed: ['check', 'var(--success-soft)', 'var(--success-text)'],
  print: ['printer', 'var(--accent-soft)', 'var(--accent-text)'],
  scan: ['scan-line', 'var(--surface-3)', 'var(--fg-2)'],
  error: ['alert-circle', 'var(--danger-soft)', 'var(--danger-text)'],
  ship: ['truck', 'var(--accent-soft)', 'var(--accent-text)'],
  start: ['play', 'var(--surface-3)', 'var(--fg-2)'],
};

export function LiveFeed({ feed }: { feed: { kind: string; t: string; who: string; ago: string }[] }) {
  if (!feed.length) {
    return <div className="empty" style={{ padding: '34px 16px' }}><div className="ei"><Icon name="coffee" size={26} /></div><h3>Brak aktywności</h3></div>;
  }
  return (
    <div className="feed">
      {feed.map((f, i) => {
        const [ic, bg, fg] = FEED_ICON[f.kind] || FEED_ICON.scan;
        return (
          <div className="feed-row" key={f.t + i}>
            <div className="ic" style={{ background: bg, color: fg }}><Icon name={ic} size={15} /></div>
            <div className="t"><b>{f.t}</b><div className="muted" style={{ fontSize: 12 }}>{f.who}</div></div>
            <div className="ago">{f.ago}</div>
          </div>
        );
      })}
    </div>
  );
}

/* Ranking celow wysylek (miasta / wojewodztwa) z paskiem udzialu. */
export function RankCard({ title, icon, rows, empty }: { title: string; icon: string; rows: RankRow[]; empty: string }) {
  const max = rows[0]?.count || 1;
  return (
    <div className="card">
      <div className="card-head"><h3><Icon name={icon} size={15} style={{ verticalAlign: '-2px', marginRight: 7 }} />{title}</h3></div>
      {!rows.length ? (
        <div className="empty" style={{ padding: '22px 16px' }}><h3 style={{ fontSize: 13 }}>{empty}</h3></div>
      ) : (
        <div className="rank-list">
          {rows.map((r, i) => (
            <div className="rank-row" key={r.name}>
              <span className="rank-pos num">{i + 1}</span>
              <div className="rank-main">
                <div className="rank-head">
                  <span className="rank-name">{r.name}</span>
                  <span className="rank-count num">{r.count} · {Math.round(r.share * 100)}%</span>
                </div>
                <div className="prog"><i style={{ width: `${(r.count / max) * 100}%` }} /></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Czas trwania dla kafla „Śr. czas pakowania". Skala dobierana dynamicznie, bo „1590s" nikomu
 * nic nie mówi: do minuty sekundy, do godziny minuty (z sekundami, gdy są), wyżej godziny.
 *
 * W systemie krążą TRZY kształty tej wartości: /orders/stats/dashboard (źródło tego kafla)
 * oddaje sekundy JAKO TEKST („1590s"), moduł statystyk liczbę, a starsze
 * /statistics gotowy zapis w rodzaju „26:30". Tekst czysto liczbowy (z opcjonalnym „s")
 * liczymy więc jak sekundy; gotowy zapis zostawiamy nietknięty.
 */
export function fmtPackingTime(value?: number | string): string {
  if (typeof value === 'string') {
    const raw = value.trim();
    if (!raw) return '—';
    const seconds = raw.match(/^(\d+(?:[.,]\d+)?)\s*s?$/i)?.[1];
    return seconds ? fmtPackingTime(Number(seconds.replace(',', '.'))) : raw;
  }
  const total = Math.max(0, Math.round(Number(value) || 0));
  if (!total) return '—';
  if (total < 60) return `${total} s`;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return m ? `${h} h ${m} min` : `${h} h`;
  return s ? `${m} min ${s} s` : `${m} min`;
}

export function StationsTab({ stations }: { stations: ActiveStation[] }) {
  if (!stations.length) {
    return <div className="empty" style={{ padding: '34px 16px' }}><div className="ei"><Icon name="cpu" size={26} /></div><h3>Brak skonfigurowanych stacji</h3><p>Stacje dodasz w Ustawieniach.</p></div>;
  }
  return (
    <div>
      {stations.map((s) => {
        const sess = s.active_session;
        const active = !!sess;
        // Rozdzielamy „nie pakuje" od „nie odpowiada": bezczynna stacja czeka na zamówienie,
        // offline znaczy wyłączony komputer i nikt tam nie spakuje.
        const offline = s.online === false;
        const done = sess?.items_packed || 0;
        const total = sess?.total_items || 0;
        return (
          <div className="sess-card" key={s.station_id || s.id}>
            <div className="op" style={!active ? { background: 'var(--surface-3)', color: 'var(--fg-3)' } : undefined}>
              {active ? initialsOf(sess?.user_name) : '—'}
            </div>
            <div className="info">
              <div className="o">{s.station_id}</div>
              <div className="s">{s.location || s.name} · {sess?.user_name || '—'}</div>
            </div>
            {active ? (
              <>
                <div className="flex" style={{ flex: 1 }}>
                  <span className="mono num" style={{ fontSize: 13, color: 'var(--fg-2)' }}>{sess?.order_name}</span>
                  <div className={`prog ${total > 0 && done >= total ? 'full' : ''}`} style={{ flex: 1, maxWidth: 200 }}>
                    <i style={{ width: `${total ? (done / total) * 100 : 0}%`, transition: 'width .8s var(--ease-out)' }} />
                  </div>
                  <span className="mono num muted" style={{ fontSize: 12 }}>{done}/{total}</span>
                </div>
                <span className="pill pill-info"><Icon name="loader" size={12} /> Pakuje</span>
              </>
            ) : (
              <><div style={{ flex: 1 }} />{offline
                ? <span className="pill pill-err"><Icon name="alert-triangle" size={12} /> Offline</span>
                : <span className="pill pill-neutral"><Icon name="moon" size={12} /> Bezczynna</span>}</>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function SessionsTab({ sessions }: { sessions: ActiveSession[] }) {
  if (!sessions.length) {
    return <div className="empty" style={{ padding: '34px 16px' }}><div className="ei"><Icon name="coffee" size={26} /></div><h3>Brak aktywnych sesji</h3></div>;
  }
  return (
    <div>
      {sessions.map((s) => {
        const pct = s.total_items ? Math.round((s.items_packed / s.total_items) * 100) : 0;
        return (
          <div className="sess-card" key={s.id}>
            <div className="op">{initialsOf(s.user_name)}</div>
            <div className="info"><div className="o">{s.order_name}</div><div className="s">{s.user_name || '—'} · {s.station_name || '—'}</div></div>
            <div className={`prog ${pct >= 100 ? 'full' : ''}`} style={{ flex: 1, maxWidth: 320 }}>
              <i style={{ width: `${pct}%`, transition: 'width .8s var(--ease-out)' }} />
            </div>
            <span className="mono num" style={{ width: 96, textAlign: 'right', fontSize: 13 }}>{s.items_packed}/{s.total_items} · {pct}%</span>
          </div>
        );
      })}
    </div>
  );
}

export function HistoryTab({ rows, onPrint }: { rows: HistoryRow[]; onPrint: (orderId: string | number) => void }) {
  if (!rows.length) {
    return <div className="empty" style={{ padding: '34px 16px' }}><div className="ei"><Icon name="history" size={26} /></div><h3>Brak historii pakowania</h3></div>;
  }
  return (
    <div className="tablewrap">
      <table className="tbl">
        <thead><tr><th>Zamówienie</th><th>Klient</th><th>Operator</th><th>Stacja</th><th>Czas</th><th>Poz.</th><th>Paczki</th><th></th></tr></thead>
        <tbody>
          {rows.map((h) => (
            <tr className="row" key={h.id}>
              <td className="mono" style={{ fontWeight: 600 }}>{h.order_name}</td>
              <td>{h.customer_name || '—'}</td>
              <td>{h.user_name || '—'}</td>
              <td className="mono">{h.station_name || '—'}</td>
              <td className="mono num">{fmtDuration(h.started_at, h.completed_at)}</td>
              <td className="num">{h.total_items ?? '—'}</td>
              <td className="num">{h.packages_count ?? 0}</td>
              <td>
                <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); onPrint(h.order_id); }}>
                  <Icon name="printer" size={14} /> Druk
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---- tryb sciany (TV dla hali pakowni) ---- */
function Wallboard({ onClose, orgLabel }: { onClose: () => void; orgLabel: string }) {
  const { data: stats } = useDashboardStats();
  const { data: stations = [] } = useActiveStations();
  const { data: sessions = [] } = useActiveSessions();
  const { data: history = [] } = usePackingHistory();
  const { data: orders = [] } = useAdminOrders();
  const deliveries = useDeliveries(orders, sessions);
  const hub = useSenderHub();
  // Mapa = kolejka do spakowania + paczki NADANE z UCS. Bez drugiego składnika „w drodze"
  // strukturalnie pokazywało 0, bo nadana przesyłka wypada z kolejki zamówień.
  const { data: dispatched = [] } = useShipmentsInTransit();
  const queueParcels = useTrackedParcels(orders, sessions, hub);
  const dispatchedParcels = useDispatchedParcels(dispatched, hub);
  const parcels = useLiveTracking(useMemo(() => [...queueParcels, ...dispatchedParcels], [queueParcels, dispatchedParcels]));
  const feed = useLiveFeed(sessions, history);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Oczekujące = długość kolejki UCS, gdy backend nie zwraca pendingOrders
  // (Shopify-direct wyłączony na rzecz UCS) — spójnie z głównym dashboardem.
  // Number(...) zabezpiecza przed "0"/null z API (string jest truthy i psuł fallback).
  const pending = Number(stats?.pendingOrders) || orders.length;
  const kpis: [string, string, React.ReactNode][] = [
    ['clock', 'Oczekujące', pending],
    ['package-check', 'Spakowane dziś', stats?.packedToday ?? 0],
    ['timer', 'Śr. czas', fmtPackingTime(stats?.avgPackingTime)],
    ['tag', 'Etykiety dziś', stats?.labelsToday ?? 0],
  ];

  return (
    <div data-theme="dark" style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'var(--bg-deep)', color: 'var(--fg-1)', display: 'flex', flexDirection: 'column', padding: 28, gap: 20 }}>
      <div className="flex" style={{ gap: 14 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--accent)', color: '#fff', display: 'grid', placeItems: 'center' }}><Icon name="package-check" size={24} /></div>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 26, letterSpacing: '-0.02em' }}>PackFlow · {orgLabel}</div>
          <div className="muted" style={{ fontSize: 13 }}>Tryb ściany — hala pakowni · na żywo</div>
        </div>
        <span className="pill pill-ok" style={{ marginLeft: 10 }}><Icon name="circle" size={9} /> LIVE</span>
        <button className="btn btn-ghost right" onClick={onClose} style={{ background: 'var(--surface)' }}><Icon name="x" size={16} /> Zamknij · Esc</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        {kpis.map(([ic, lbl, val]) => (
          <div key={lbl} className="kpi" style={{ background: 'var(--surface)', textAlign: 'center' }}>
            <div className="lbl" style={{ justifyContent: 'center' }}><Icon name={ic} size={15} /> {lbl}</div>
            <div className="val" style={{ fontSize: 58 }}>{val}</div>
          </div>
        ))}
      </div>

      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20, minHeight: 0 }}>
        <div className="card" style={{ background: 'var(--surface)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <DeliveryMap deliveries={deliveries} parcels={parcels} hub={hub} wall />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0 }}>
          <div className="card" style={{ background: 'var(--surface)' }}>
            <div className="card-head"><h3>Stacje</h3></div>
            <StationsTab stations={stations} />
          </div>
          <div className="card" style={{ background: 'var(--surface)', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="card-head"><h3>Aktywność</h3><span className="pill pill-info"><Icon name="circle" size={9} /> live</span></div>
            <div style={{ overflowY: 'auto' }}><LiveFeed feed={feed.slice(0, 6)} /></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [tab, setTab] = useState<'stacje' | 'sesje' | 'historia'>('stacje');
  const [wall, setWall] = useState(false);
  const { orgSlug, displayName } = useOrg();
  const push = useAdminToast();

  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: stations = [] } = useActiveStations();
  const { data: sessions = [] } = useActiveSessions();
  const { data: history = [] } = usePackingHistory();
  const { data: orders = [] } = useAdminOrders();
  const deliveries = useDeliveries(orders, sessions);
  const hub = useSenderHub();
  // Mapa = kolejka do spakowania + paczki NADANE z UCS, tak samo jak w trybie ściany.
  // Sam widok kolejki pokazywał „0 w drodze", bo nadana przesyłka z kolejki wypada.
  const { data: dispatched = [] } = useShipmentsInTransit();
  const queueParcels = useTrackedParcels(orders, sessions, hub);
  const dispatchedParcels = useDispatchedParcels(dispatched, hub);
  const parcels = useLiveTracking(
    useMemo(() => [...queueParcels, ...dispatchedParcels], [queueParcels, dispatchedParcels]),
  );
  const feed = useLiveFeed(sessions, history);
  const { cities: topCities, regions: topRegions, countries: topCountries } = useDestinationRankings(orders);
  const orgLabel = displayName || orgSlug || 'organizacja';

  async function printAllDocs(orderId: string | number) {
    try {
      await axios.post(`${API_URL}/api/printing/print-product-labels`, { orderId });
      await axios.post(`${API_URL}/api/printing/print-packing-list`, { orderId });
      const hist = (await axios.get(`${API_URL}/api/packing/order/${orderId}/history`)).data;
      const carriers = new Set<string>((hist.packages || []).map((p: any) => p.carrier).filter(Boolean));
      const carrierArr = Array.from(carriers);
      for (const carrier of carrierArr) {
        await axios.post(`${API_URL}/api/printing/print-shipping-labels`, { orderId, carrier });
      }
      push('Komplet dokumentów wysłany do druku');
    } catch (e: any) {
      push(e?.response?.data?.error || 'Błąd drukowania dokumentów', 'err');
    }
  }

  if (statsLoading && !stats) return <PageSkeleton />;

  // „Aktywne" = stanowiska PODŁĄCZONE (świeży heartbeat aplikacji stacji) — tak liczy je
  // obsługa hali. Gdy API jest starsze i nie podaje `online`, spadamy na dawne kryterium
  // (otwarta sesja), żeby licznik nigdy nie pokazał zera przy widocznej pracy.
  const activeCount = stations.some((s) => s.online !== undefined)
    ? stations.filter((s) => s.online).length
    : stations.filter((s) => !!s.active_session).length;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Dashboard</h1>
          <div className="ph-sub">{orgLabel} — przegląd operacyjny na żywo</div>
        </div>
        <div className="actions">
          <button className="btn btn-ghost" onClick={() => setWall(true)}><Icon name="tv" size={16} /> Tryb ściany</button>
          <button className="btn btn-ghost"><Icon name="calendar" size={16} /> Dziś</button>
        </div>
      </div>

      <div className="kpi-row" style={{ marginBottom: 16 }}>
        <KpiCard icon="clock" label="Oczekujące" value={Number(stats?.pendingOrders) || orders.length} />
        <KpiCard icon="package-check" label="Spakowane dziś" value={stats?.packedToday ?? 0} />
        <KpiCard icon="timer" label="Śr. czas pakowania" value={fmtPackingTime(stats?.avgPackingTime)} />
        <KpiCard icon="tag" label="Etykiety dziś" value={stats?.labelsToday ?? 0} />
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1.55fr 1fr', alignItems: 'start' }}>
        <div className="grid" style={{ gap: 16 }}>
          <DeliveryMap deliveries={deliveries} parcels={parcels} packedToday={stats?.packedToday} hub={hub} />
          <div className="card">
            <div className="card-head" style={{ paddingBottom: 0, borderBottom: 0 }}>
              <div className="tabs" style={{ marginBottom: 0, border: 0 }}>
                <button className={`tab ${tab === 'stacje' ? 'active' : ''}`} onClick={() => setTab('stacje')}><Icon name="cpu" size={15} /> Aktywne stacje <span className="cnt">{activeCount}</span></button>
                <button className={`tab ${tab === 'sesje' ? 'active' : ''}`} onClick={() => setTab('sesje')}><Icon name="activity" size={15} /> Sesje</button>
                <button className={`tab ${tab === 'historia' ? 'active' : ''}`} onClick={() => setTab('historia')}><Icon name="history" size={15} /> Historia</button>
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--divider)' }}>
              {tab === 'stacje' && <StationsTab stations={stations} />}
              {tab === 'sesje' && <SessionsTab sessions={sessions} />}
              {tab === 'historia' && <HistoryTab rows={history} onPrint={printAllDocs} />}
            </div>
          </div>
        </div>

        <div className="grid" style={{ gap: 16, position: 'sticky', top: 0 }}>
          <div className="card">
            <div className="card-head"><h3>Aktywność na żywo</h3><span className="pill pill-info"><Icon name="circle" size={9} /> live</span></div>
            <LiveFeed feed={feed} />
          </div>
          <RankCard title="Top miasta wysyłek" icon="map-pin" rows={topCities} empty="Brak danych o wysyłkach" />
          <RankCard title="Top regiony" icon="map" rows={topRegions} empty="Brak danych o regionach" />
          <RankCard title="Top kraje" icon="globe" rows={topCountries} empty="Brak danych o krajach" />
        </div>
      </div>

      {wall && <Wallboard onClose={() => setWall(false)} orgLabel={orgLabel} />}
    </div>
  );
}
